import { z } from 'zod'
import { textoObrigatorio } from './comum'

/**
 * Chatbot (seção 6). Construtor visual por diagrama, no modelo do Kommo.
 *
 * É o item mais caro do projeto e também obrigatório da v1, porque a
 * distribuição do rodízio é configurada dentro dele.
 *
 * O diagrama é salvo como JSON em `chatbot_fluxos.definicao`; o motor lê esse
 * JSON e mantém o estado em `chatbot_execucoes`.
 */

/**
 * Como o bloco Distribuir escolhe quem atende.
 *
 * Era só rodízio, fixo no código. Mas a forma justa depende da operação: com
 * conversas de peso parecido o rodízio é o certo; com umas de dez minutos e
 * outras de duas horas, quem tem menos conversa aberta é uma medida melhor de
 * quem está livre.
 */
export const MODOS_DISTRIBUICAO = ['rodizio', 'menos_conversas', 'especifico', 'so_fila'] as const
export type ModoDistribuicao = (typeof MODOS_DISTRIBUICAO)[number]

export const DISTRIBUICAO: Record<ModoDistribuicao, { rotulo: string; explicacao: string }> = {
  rodizio: {
    rotulo: 'Rodízio 1 para 1',
    explicacao:
      'Dá a vez ao próximo da lista do setor, em ordem fixa. Justo quando as conversas têm peso parecido. Pula quem está pausado, em folga ou offline.',
  },
  menos_conversas: {
    rotulo: 'Quem tem menos conversa aberta',
    explicacao:
      'Escolhe quem está com menos chamados abertos no momento. Melhor quando uma conversa leva dez minutos e outra leva duas horas. O rodízio trataria as duas como iguais.',
  },
  especifico: {
    rotulo: 'Sempre a mesma pessoa',
    explicacao:
      'Entrega tudo deste caminho para quem você escolher. Serve para um funil que só uma pessoa atende, ou para separar um cliente grande.',
  },
  so_fila: {
    rotulo: 'Deixar na fila do setor',
    explicacao:
      'Marca o setor e não escolhe ninguém: a conversa fica na fila para quem pegar primeiro. Para equipes que preferem puxar o trabalho em vez de receber.',
  },
}

export const TIPOS_NO = [
  'inicio',
  'enviar_mensagem',
  'menu',
  'condicao',
  'distribuir',
  'aguardar_resposta',
  'salvar_campo',
  'aplicar_etiqueta',
  'entregar_humano',
] as const

export type TipoNo = (typeof TIPOS_NO)[number]

export interface DefinicaoBloco {
  rotulo: string
  descricao: string
  /**
   * Um caso concreto, do jeito que apareceria numa conversa de verdade.
   *
   * A descrição diz o que o bloco faz; o exemplo diz para que serve. Quem
   * monta o primeiro fluxo lê "Desvia por valor de resposta ou por campo do
   * contato" e continua sem saber quando usaria isso.
   */
  exemplo: string
  /** Como a saída se comporta, para não precisar descobrir arrastando. */
  saidaExplicada?: string
  /** Saídas fixas. Menu e condição geram saídas conforme a configuração. */
  saidas: { id: string; rotulo: string }[]
  /** Bloco que termina o fluxo: não tem para onde seguir. */
  terminal?: boolean
  /** Bloco que pausa esperando o cliente. */
  aguarda?: boolean
  cor: string
}

export const BLOCOS: Record<TipoNo, DefinicaoBloco> = {
  inicio: {
    rotulo: 'Início',
    descricao: 'Por onde o fluxo começa. Só pode existir um.',
    exemplo:
      'Todo fluxo tem um. Quando um número novo manda a primeira mensagem e não tem dono, é por aqui que o bot entra.',
    saidas: [{ id: 'saida', rotulo: '' }],
    cor: '#16a34a',
  },
  enviar_mensagem: {
    rotulo: 'Enviar mensagem',
    descricao: 'Manda um texto para o cliente e segue adiante.',
    exemplo:
      '"Olá! Você chegou na FaroEscolar. Já chamo alguém para te atender." O bot manda e segue sozinho para o próximo bloco.',
    saidas: [{ id: 'saida', rotulo: '' }],
    cor: '#1f6feb',
  },
  menu: {
    rotulo: 'Menu de opções',
    descricao: 'Pergunta com alternativas numeradas e ramifica pela escolha.',
    exemplo:
      '"Sobre o que você precisa falar? 1. Matrícula e valores 2. Dúvidas sobre a escola". O cliente responde 1 ou 2, ou escreve o texto da opção.',
    saidaExplicada: 'Uma saída por opção. Ligue cada uma ao que deve acontecer naquele caminho.',
    saidas: [],
    aguarda: true,
    cor: '#7c3aed',
  },
  condicao: {
    rotulo: 'Condição',
    descricao: 'Desvia por valor de resposta ou por campo do contato.',
    exemplo:
      'Se o campo Segmento do contato for "Odontologia", manda para o vendedor de odonto; se não, segue o caminho comum.',
    saidaExplicada: 'Duas saídas: sim e não. As duas precisam levar a algum lugar.',
    saidas: [
      { id: 'sim', rotulo: 'sim' },
      { id: 'nao', rotulo: 'não' },
    ],
    cor: '#d97706',
  },
  distribuir: {
    rotulo: 'Distribuir',
    descricao: 'Escolhe quem atende no setor e entrega a conversa para essa pessoa.',
    exemplo:
      'Escolhe quem atende, seja por rodízio, por quem tem menos conversa aberta ou sempre a mesma pessoa. Entrega a conversa e grava o dono da carteira.',
    saidaExplicada: 'Não tem saída: é o fim natural de um fluxo de triagem.',
    saidas: [],
    terminal: true,
    cor: '#dc2626',
  },
  aguardar_resposta: {
    rotulo: 'Aguardar resposta',
    descricao: 'Pausa esperando o cliente, com tempo limite.',
    exemplo:
      'Pergunte "qual o CNPJ da empresa?" no bloco anterior e use este para esperar. Passados 10 minutos sem resposta, sai pelo caminho do tempo.',
    saidaExplicada:
      'Duas saídas: respondeu e estourou o tempo. Sem prazo, a segunda nunca acontece.',
    saidas: [
      { id: 'resposta', rotulo: 'respondeu' },
      { id: 'tempo', rotulo: 'estourou o tempo' },
    ],
    aguarda: true,
    cor: '#9333ea',
  },
  salvar_campo: {
    rotulo: 'Salvar em campo',
    descricao: 'Grava a última resposta em um campo do contato.',
    exemplo:
      'Depois de um menu com os segmentos, grava a escolha no campo Segmento do contato. O vendedor já pega a conversa sabendo com quem fala.',
    saidas: [{ id: 'saida', rotulo: '' }],
    cor: '#0d9488',
  },
  aplicar_etiqueta: {
    rotulo: 'Aplicar etiqueta',
    descricao: 'Marca o contato com uma etiqueta.',
    exemplo:
      'Marca "Veio do bot" ou "Pediu orçamento", para depois filtrar essas conversas na lista.',
    saidas: [{ id: 'saida', rotulo: '' }],
    cor: '#be185d',
  },
  entregar_humano: {
    rotulo: 'Entregar para humano',
    descricao: 'Encerra o bot e deixa o chamado para a equipe.',
    exemplo:
      '"Já passo para a equipe." Encerra o bot sem escolher vendedor. Use quando a distribuição for feita de outro jeito.',
    saidaExplicada: 'Não tem saída: o fluxo termina aqui.',
    saidas: [],
    terminal: true,
    cor: '#475569',
  },
}

/** Blocos que a especificação deixou para depois (seção 6). */
export const BLOCOS_FUTUROS = [
  'Mudar etapa do funil',
  'Esperar X horas antes do próximo bloco',
  'Chamada para API externa',
]

export interface OpcaoMenu {
  chave: string
  rotulo: string
}

export interface NoFluxo {
  id: string
  tipo: TipoNo
  posicao: { x: number; y: number }
  dados: {
    texto?: string
    pergunta?: string
    opcoes?: OpcaoMenu[]
    /** `resposta` ou `campo:<chave>` */
    origem?: string
    operador?: 'igual' | 'contem' | 'existe' | 'vazio'
    valor?: string
    minutos?: number
    setorId?: string
    /** Bloco Distribuir: como escolher quem atende. Sem valor, é o rodízio. */
    modo?: ModoDistribuicao
    /** Quem atende quando o modo é `especifico`. */
    usuarioId?: string
    tagId?: string
    chaveCampo?: string
  }
}

export interface ArestaFluxo {
  id: string
  origem: string
  /** Saída do bloco: `saida`, `sim`, `nao`, `opcao:<chave>`… */
  saida: string
  destino: string
}

export interface DefinicaoFluxo {
  nos: NoFluxo[]
  arestas: ArestaFluxo[]
}

export const definicaoFluxoSchema = z.object({
  nos: z.array(
    z.object({
      id: z.string().min(1),
      tipo: z.enum(TIPOS_NO),
      posicao: z.object({ x: z.number(), y: z.number() }),
      dados: z.record(z.string(), z.unknown()).default({}),
    }),
  ),
  arestas: z.array(
    z.object({
      id: z.string().min(1),
      origem: z.string().min(1),
      saida: z.string().min(1),
      destino: z.string().min(1),
    }),
  ),
})

export const criarFluxoSchema = z.object({
  nome: textoObrigatorio('nome do fluxo', 120),
  conexaoId: z.string().uuid().optional().nullable(),
  /**
   * Modelo de partida. Sem ele, nasce só com o bloco de início.
   *
   * A validação da chave é do servidor, contra a lista de modelos: aceitar um
   * `definicao` inteiro vindo do cliente aqui abriria uma porta para gravar
   * diagrama arbitrário sem passar pela conferência.
   */
  modelo: z.string().max(40).optional(),
})
export type CriarFluxoEntrada = z.infer<typeof criarFluxoSchema>

export const salvarFluxoSchema = z.object({
  nome: textoObrigatorio('nome do fluxo', 120).optional(),
  conexaoId: z.string().uuid().optional().nullable(),
  ativo: z.boolean().optional(),
  definicao: definicaoFluxoSchema.optional(),
})
export type SalvarFluxoEntrada = z.infer<typeof salvarFluxoSchema>

export interface FluxoDto {
  id: string
  nome: string
  conexaoId: string | null
  conexaoNome: string | null
  ativo: boolean
  versao: number
  definicao: DefinicaoFluxo
  execucoesRodando: number
  criadoEm: string
  atualizadoEm: string
}

export interface ProblemaFluxo {
  noId: string | null
  gravidade: 'erro' | 'aviso'
  mensagem: string
}

/**
 * Conferência do diagrama antes de ativar.
 *
 * Um fluxo quebrado não dá erro visível: ele simplesmente para no meio e o
 * cliente fica sem resposta. Por isso a validação roda no servidor **e** na
 * tela, e ativar com erro é bloqueado.
 */
export function conferirFluxo(definicao: DefinicaoFluxo): ProblemaFluxo[] {
  const problemas: ProblemaFluxo[] = []
  const { nos, arestas } = definicao

  const inicios = nos.filter((n) => n.tipo === 'inicio')
  if (inicios.length === 0) {
    problemas.push({
      noId: null,
      gravidade: 'erro',
      mensagem: 'O fluxo precisa de um bloco de início.',
    })
  }
  if (inicios.length > 1) {
    problemas.push({ noId: null, gravidade: 'erro', mensagem: 'Há mais de um bloco de início.' })
  }

  const porId = new Map(nos.map((n) => [n.id, n]))

  for (const aresta of arestas) {
    if (!porId.has(aresta.origem) || !porId.has(aresta.destino)) {
      problemas.push({
        noId: aresta.origem,
        gravidade: 'erro',
        mensagem: 'Há uma ligação apontando para bloco que não existe mais.',
      })
    }
  }

  for (const no of nos) {
    const bloco = BLOCOS[no.tipo]
    const saidasUsadas = new Set(arestas.filter((a) => a.origem === no.id).map((a) => a.saida))

    /*
      Antes do `continue` dos terminais de propósito: `distribuir` é terminal,
      e a versão anterior desta conferência ficava depois — nunca rodava.

      "Sempre a mesma pessoa" sem pessoa escolhida cai na fila em toda
      conversa: o bloco parece configurado e não distribui nada.
    */
    if (no.tipo === 'distribuir' && no.dados.modo === 'especifico' && !no.dados.usuarioId) {
      problemas.push({
        noId: no.id,
        gravidade: 'erro',
        mensagem: 'Escolha quem atende: o modo é "sempre a mesma pessoa" e nenhuma foi indicada.',
      })
    }

    if (bloco.terminal) continue

    if (no.tipo === 'menu') {
      const opcoes = (no.dados.opcoes ?? []) as OpcaoMenu[]
      if (opcoes.length === 0) {
        problemas.push({ noId: no.id, gravidade: 'erro', mensagem: 'O menu está sem opções.' })
      }
      for (const opcao of opcoes) {
        if (!saidasUsadas.has(`opcao:${opcao.chave}`)) {
          problemas.push({
            noId: no.id,
            gravidade: 'aviso',
            mensagem: `A opção "${opcao.rotulo}" não leva a lugar nenhum.`,
          })
        }
      }
      if (!no.dados.pergunta) {
        problemas.push({ noId: no.id, gravidade: 'erro', mensagem: 'O menu está sem pergunta.' })
      }
      continue
    }

    for (const saida of bloco.saidas) {
      if (!saidasUsadas.has(saida.id)) {
        problemas.push({
          noId: no.id,
          gravidade: 'aviso',
          mensagem: saida.rotulo
            ? `A saída "${saida.rotulo}" de ${bloco.rotulo} está solta.`
            : `${bloco.rotulo} não leva a lugar nenhum.`,
        })
      }
    }

    if (no.tipo === 'enviar_mensagem' && !no.dados.texto) {
      problemas.push({ noId: no.id, gravidade: 'erro', mensagem: 'A mensagem está vazia.' })
    }
    if (no.tipo === 'salvar_campo' && !no.dados.chaveCampo) {
      problemas.push({ noId: no.id, gravidade: 'erro', mensagem: 'Escolha o campo onde gravar.' })
    }
    if (no.tipo === 'aplicar_etiqueta' && !no.dados.tagId) {
      problemas.push({ noId: no.id, gravidade: 'erro', mensagem: 'Escolha a etiqueta.' })
    }

    /*
      Condição sem alvo compara com string vazia e cai sempre no mesmo ramo.
      Não quebra o fluxo — o que é pior: ele anda, e sempre para o lado errado.
    */
    if (no.tipo === 'condicao') {
      const operador = no.dados.operador ?? 'igual'
      const precisaDeAlvo = operador === 'igual' || operador === 'contem'
      if (precisaDeAlvo && !String(no.dados.valor ?? '').trim()) {
        problemas.push({
          noId: no.id,
          gravidade: 'erro',
          mensagem: 'A condição não diz com o que comparar, então vai cair sempre no mesmo ramo.',
        })
      }
    }

    if (no.tipo === 'aguardar_resposta' && !no.dados.minutos) {
      problemas.push({
        noId: no.id,
        gravidade: 'aviso',
        mensagem:
          'Sem tempo limite, a espera não estoura e a saída "estourou o tempo" nunca é usada.',
      })
    }
  }

  // Chave repetida faz duas opções apontarem para a mesma saída, e uma delas
  // nunca é alcançada.
  for (const no of nos.filter((n) => n.tipo === 'menu')) {
    const chaves = ((no.dados.opcoes ?? []) as OpcaoMenu[]).map((o) => o.chave)
    const repetidas = chaves.filter((ch, i) => chaves.indexOf(ch) !== i)
    if (repetidas.length > 0) {
      problemas.push({
        noId: no.id,
        gravidade: 'erro',
        mensagem: `Duas opções do menu têm a mesma chave (${[...new Set(repetidas)].join(', ')}).`,
      })
    }
  }

  // Bloco solto no meio do canvas costuma ser edição pela metade.
  const alcancados = new Set<string>()
  const fila = inicios.map((n) => n.id)
  while (fila.length > 0) {
    const atual = fila.shift() as string
    if (alcancados.has(atual)) continue
    alcancados.add(atual)
    for (const aresta of arestas.filter((a) => a.origem === atual)) fila.push(aresta.destino)
  }
  for (const no of nos) {
    if (!alcancados.has(no.id)) {
      problemas.push({
        noId: no.id,
        gravidade: 'aviso',
        mensagem: `${BLOCOS[no.tipo].rotulo} não é alcançável a partir do início.`,
      })
    }
  }

  /**
   * Laço sem ponto de espera.
   *
   * Um ciclo em que nenhum bloco pausa para ouvir o cliente é um laço que o
   * motor percorre sem parar: cada volta manda outra mensagem, e o cliente
   * recebe uma rajada até alguém desligar o fluxo. É o pior defeito possível
   * aqui, porque acontece com o cliente do outro lado e derruba o número.
   *
   * Ciclo que passa por menu ou por "aguardar resposta" é legítimo — é como
   * se volta ao menu principal.
   */
  for (const ciclo of ciclosSemEspera(nos, arestas)) {
    problemas.push({
      noId: ciclo[0] ?? null,
      gravidade: 'erro',
      mensagem:
        'Há um laço que não espera resposta em nenhum ponto: ' +
        `${ciclo.map((id) => BLOCOS[porId.get(id)?.tipo ?? 'inicio'].rotulo).join(' → ')}. ` +
        'O motor daria voltas mandando mensagem sem parar.',
    })
  }

  /*
    Caminho que morre no meio.

    Bloco alcançável, não terminal e sem nenhuma saída ligada deixa o cliente
    esperando uma resposta que não vem — e o chamado sem dono, porque o fluxo
    nunca chegou a distribuir.
  */
  const temSaida = new Set(arestas.map((a) => a.origem))
  /*
    Fluxo com só o bloco de início é fluxo recém-criado, não fluxo quebrado.

    Cobrar "o cliente fica esperando" de quem acabou de clicar em criar é
    ruído: nada foi montado ainda. Vira um aviso que explica o próximo passo.
  */
  const soTemInicio = nos.length === 1 && nos[0]?.tipo === 'inicio'
  if (soTemInicio) {
    problemas.push({
      noId: nos[0]?.id ?? null,
      gravidade: 'aviso',
      mensagem: 'O fluxo ainda está vazio: arraste um bloco e ligue ao início.',
    })
  }

  for (const no of nos) {
    if (soTemInicio) break
    if (!alcancados.has(no.id) || BLOCOS[no.tipo].terminal || temSaida.has(no.id)) continue
    problemas.push({
      noId: no.id,
      gravidade: 'erro',
      mensagem: `${BLOCOS[no.tipo].rotulo} é o fim de um caminho, mas não entrega o atendimento. O cliente fica esperando.`,
    })
  }

  return problemas
}

/**
 * Ciclos alcançáveis em que nenhum bloco pausa.
 *
 * Busca em profundidade guardando o caminho: ao reencontrar um nó que já está
 * na pilha, o trecho entre a primeira aparição e agora é o ciclo. Só interessa
 * o que não tem bloco de espera dentro.
 */
function ciclosSemEspera(nos: NoFluxo[], arestas: ArestaFluxo[]): string[][] {
  const porId = new Map(nos.map((n) => [n.id, n]))
  const vizinhos = new Map<string, string[]>()
  for (const a of arestas) {
    vizinhos.set(a.origem, [...(vizinhos.get(a.origem) ?? []), a.destino])
  }

  const encontrados: string[][] = []
  const jaRelatados = new Set<string>()
  const visitados = new Set<string>()

  function andar(id: string, caminho: string[]) {
    const naPilha = caminho.indexOf(id)
    if (naPilha !== -1) {
      const ciclo = caminho.slice(naPilha)
      if (ciclo.every((n) => !BLOCOS[porId.get(n)?.tipo ?? 'inicio'].aguarda)) {
        // Assinatura pela ordem canônica, para o mesmo ciclo não sair duas
        // vezes só porque foi alcançado por outro caminho.
        const assinatura = [...ciclo].sort().join('>')
        if (!jaRelatados.has(assinatura)) {
          jaRelatados.add(assinatura)
          encontrados.push(ciclo)
        }
      }
      return
    }
    if (visitados.has(id)) return
    visitados.add(id)
    for (const proximo of vizinhos.get(id) ?? []) andar(proximo, [...caminho, id])
  }

  for (const inicio of nos.filter((n) => n.tipo === 'inicio')) andar(inicio.id, [])
  return encontrados
}

export function fluxoTemErro(problemas: ProblemaFluxo[]): boolean {
  return problemas.some((p) => p.gravidade === 'erro')
}
